/* eslint-disable filename-rules/match */
import { mean } from "lodash-es";
import { useEffect } from "react";

import { SubmissionType } from "components/server-types";
import { storeApi, useAppSelector } from "store/index";
import {
  selectAllCourseUsersByUserId,
  selectAssessments,
  selectOutcomeAssessments,
  selectPageGroupUsers,
  selectPageOutcomes,
  selectPages,
  selectSubmissions,
} from "store/selectors";
import { isGroupPage } from "utils/page-utils";

/**
 * Returns a map of outcome IDs to their average scores for a given user, aggregated across all
 * assessed pages across all courses the user is enrolled in.
 */
export const useStudentAverageOutcomeScores = (userId: string | null): Record<string, number> => {
  useEffect(() => {
    // Note we don't need to pass in student ids for everything because the backend already
    // filters for only data the student can see.
    if (userId) {
      storeApi.page_group_users.list({ course_user__user_id: userId });
      storeApi.page_outcomes.list();
      storeApi.pages.list();
      storeApi.submissions.list();
      storeApi.assessments.list();
      storeApi.outcome_assessments.list();
    }
  }, [userId]);

  const courseUsersForUser = useAppSelector((s) => selectAllCourseUsersByUserId(s, userId));
  const courseUserIdsForUser = new Set(courseUsersForUser.map((cu) => cu.id));
  const courseMap = useAppSelector((s) => s.courses);
  const coursesForUser = courseUsersForUser.map((cu) => courseMap[cu.course_id]).filter(Boolean);
  const courseIdsForUser = new Set(coursesForUser.map((c) => c.id));
  const allPageGroupUsers = useAppSelector(selectPageGroupUsers);
  const pageGroupIdsForUser = new Set(
    allPageGroupUsers
      .filter((pgu) => courseUserIdsForUser.has(pgu.course_user_id))
      .map((pgu) => pgu.page_group_id),
  );
  const allPages = useAppSelector(selectPages);
  const assessedPagesForUser = allPages.filter(
    (p) => courseIdsForUser.has(p.course_id) && p.released_at && p.assessments_published_at,
  );

  const courseOutcomeMap = useAppSelector((s) => s.course_outcomes);
  const allPageOutcomes = useAppSelector(selectPageOutcomes);
  const allSubmissions = useAppSelector(selectSubmissions);
  const allAssessments = useAppSelector(selectAssessments);
  const allOutcomeAssessments = useAppSelector(selectOutcomeAssessments);

  const aggregatedScoresByOutcomeId = assessedPagesForUser.reduce(
    (acc, page) => {
      const isSubmissionForUser = isGroupPage(page)
        ? (s: SubmissionType) => pageGroupIdsForUser.has(s.page_group_id!)
        : (s: SubmissionType) => courseUserIdsForUser.has(s.course_user_id!);
      const officialSubmission = allSubmissions.find(
        (s) => s.page_id === page.id && s.official_at && isSubmissionForUser(s),
      );
      if (!officialSubmission) {
        return acc;
      }

      const assessment = allAssessments.find((a) => a.submission_id === officialSubmission.id);
      if (!assessment) {
        return acc;
      }

      const pageOutcomesForPage = allPageOutcomes.filter((po) => po.page_id === page.id);
      pageOutcomesForPage.forEach((pageOutcome) => {
        const courseOutcome = courseOutcomeMap[pageOutcome.course_outcome_id];
        if (!courseOutcome) {
          return;
        }
        const outcomeId = courseOutcome.outcome_id;

        const outcomeAssessment = allOutcomeAssessments.find(
          (oa) => oa.page_outcome_id === pageOutcome.id && oa.assessment_id === assessment.id,
        );
        if (!outcomeAssessment) {
          return;
        }

        if (!acc[outcomeId]) {
          // eslint-disable-next-line no-param-reassign
          acc[outcomeId] = [];
        }
        acc[outcomeId].push(outcomeAssessment.score);
      });

      return acc;
    },
    {} as Record<string, number[]>,
  );

  // Now that we have aggregated scores, calculate the average for each outcome
  return Object.fromEntries(
    Object.entries(aggregatedScoresByOutcomeId).map(([outcomeId, scores]) => {
      const averageScore = mean(scores);
      return [outcomeId, averageScore];
    }),
  );
};
